import clsx from 'clsx';
import React from 'react';

import styles from './Typography.module.css';

export type TypographyVariant =
    | 'display-1'
    | 'display-2'
    | 'heading-1'
    | 'heading-2'
    | 'heading-3'
    | 'title-1'
    | 'title-2'
    | 'body-1'
    | 'body-2'
    | 'caption'
    | 'label-1'
    | 'label-2'
    | 'tag'
    | 'link-1'
    | 'link-2';

export type TypographyColor =
    | 'brand'
    | 'primary'
    | 'secondary-1'
    | 'secondary-2'
    | 'disabled'
    | 'placeholder'
    | 'inverse'
    | 'on-accent'
    | 'action-primary'
    | 'action-primary-hover'
    | 'action-primary-pressed'
    | 'action-link'
    | 'success'
    | 'warning'
    | 'error'
    | 'info';

/** Overrides the variant's default font-weight. */
export type TypographyWeight = 'regular' | 'medium' | 'semibold';

const VARIANT_ELEMENT: Record<TypographyVariant, keyof JSX.IntrinsicElements> = {
    'display-1': 'p',
    'display-2': 'p',
    'heading-1': 'h1',
    'heading-2': 'h2',
    'heading-3': 'h3',
    'title-1': 'h4',
    'title-2': 'h5',
    'body-1': 'p',
    'body-2': 'p',
    caption: 'span',
    'label-1': 'span',
    'label-2': 'span',
    tag: 'span',
    'link-1': 'a',
    'link-2': 'a',
};

export interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
    variant: TypographyVariant;
    /**
     * Semantic text colour. `disabled` is for the text of a disabled control only: it also sets `aria-disabled`,
     * because `--text-disabled` is below 4.5:1, which WCAG allows only for text in an inactive control.
     */
    color?: TypographyColor;
    /** When set, overrides the design-token weight for this variant. */
    weight?: TypographyWeight;
    /**
     * Override the rendered HTML element. Defaults to the variant's semantic
     * element (e.g. `heading-1` → `<h1>`, `caption` → `<span>`).
     *
     * Use this when the visual style and the semantic / layout role need to
     * differ, e.g. a `caption` that should participate in a flex row as a
     * `<p>`, or a `body-1` rendered as a `<div>` to wrap block children.
     */
    component?: keyof JSX.IntrinsicElements;
    children?: React.ReactNode;
    className?: string;
}

/**
 * Renders text at a design-system type scale with optional semantic colour and
 * weight. `variant` / `color` / `weight` are emitted as `data-*` attributes and
 * resolved in `Typography.module.css` against the global tokens in
 * `tokens.css` — the same attribute-driven pattern used by Button and Chip.
 *
 * The rendered element defaults to the variant's semantic tag (`heading-1` →
 * `<h1>`); use `component` to override it when the visual and semantic roles
 * differ.
 *
 * @example
 * <Typography variant="heading-1">Account settings</Typography>
 *
 * @example
 * <Typography variant="caption" color="secondary-2" component="p">
 *     Last updated today
 * </Typography>
 */
const Typography = React.forwardRef<HTMLElement, TypographyProps>(
    ({ variant, color = 'primary', weight, component, className, children, ...rest }, ref) => {
        const Tag = (component ?? VARIANT_ELEMENT[variant]) as React.ElementType;

        return (
            <Tag
                ref={ref}
                // Disabled ink is below 4.5:1 on purpose. `aria-disabled` says the text belongs to an inactive control,
                // which WCAG 1.4.3 exempts, so a checker doesn't hold it to body-text contrast. A caller can still set
                // its own `aria-disabled` through `rest`.
                aria-disabled={color === 'disabled' || undefined}
                data-variant={variant}
                data-color={color}
                data-weight={weight}
                className={clsx(styles.typography, className)}
                {...rest}
            >
                {children}
            </Tag>
        );
    }
);

Typography.displayName = 'Typography';

export { Typography };
export default Typography;
